import { Box, Typography, useTheme, Button, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import { useState, useEffect, useMemo, useRef } from "react";
import { tokens } from "../../theme";
import { useNavigate, useLocation } from "react-router-dom";
import useResultService from "../../api/services/ResultsService";
import LoadingSpinner from "../../components/LoadingSpinner";
import generateClassResultsPDF from "../../utils/classResultsPdf";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

// ---------- O-LEVEL Grades (same as /class_results) ----------
const getGrade = (marks) => {
  if (marks >= 75) return "A";
  if (marks >= 65) return "B";
  if (marks >= 45) return "C";
  if (marks >= 30) return "D";
  return "F";
};

// ---------- O-LEVEL Grade Points (same as /class_results) ----------
const getGradePoint = (grade) => {
  switch (grade) {
    case "A":
      return 1;
    case "B":
      return 2;
    case "C":
      return 3;
    case "D":
      return 4;
    case "F":
      return 5;
    default:
      return 0;
  }
};

// ---------- O-LEVEL Division: best 7 subjects (lowest points), need ≥ 7 graded ----------
const determineDivisionPoints = (subjectsObj) => {
  const pointsArray = Object.values(subjectsObj || {})
    .filter((s) => s?.grade)
    .map((s) => getGradePoint(s.grade))
    .filter((p) => p > 0);

  if (pointsArray.length < 7) return null;

  if (pointsArray.length === 7) {
    return pointsArray.reduce((sum, point) => sum + point, 0);
  }

  const bestSeven = [...pointsArray].sort((a, b) => a - b).slice(0, 7);
  return bestSeven.reduce((sum, point) => sum + point, 0);
};

const getDivisionLabel = (points) => {
  if (points == null) return "Incomplete";
  if (points >= 1 && points <= 17) return `Division 1 . ${points}`;
  if (points >= 18 && points <= 21) return `Division 2 . ${points}`;
  if (points >= 22 && points <= 25) return `Division 3 . ${points}`;
  if (points >= 26 && points <= 33) return `Division 4 . ${points}`;
  return `Division 0`;
};

const getGradedSubjects = (subjectsObj) => {
  if (!subjectsObj) return [];

  return Object.entries(subjectsObj)
    .filter(([, details]) => {
      const hasMarks = details?.marks !== null && details?.marks !== undefined;
      const hasGrade = Boolean(details?.grade);
      return hasMarks && hasGrade;
    })
    .map(([subject, details]) => ({
      subject,
      grade: details.grade,
      points: getGradePoint(details.grade),
      marks: details.marks,
    }))
    .filter((item) => item.points > 0);
};

// GPA = sum of 7 best grade points ÷ 7 (only when student has 7+ graded subjects)
const calculateStudentGpa = (subjectsObj) => {
  const gradedSubjects = getGradedSubjects(subjectsObj);
  if (gradedSubjects.length < 7) return null;

  const top7 = [...gradedSubjects].sort((a, b) => a.points - b.points).slice(0, 7);
  const totalPoints = top7.reduce((sum, item) => sum + item.points, 0);
  return totalPoints / 7;
};

const formatGpa = (gpa) => {
  if (gpa === null || gpa === undefined || Number.isNaN(Number(gpa))) return "N/A";
  return Number(gpa).toFixed(4);
};

const AlevelClassResultsMarks100 = () => {
  const location = useLocation();
  const classId = location.state?.classId || location.state?.id;
  const examinationTypeId = location.state?.examId;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const { fetchClassResults, sendSmsToParents } = useResultService();

  // ✅ prevent infinite loops if service functions change reference
  const fetchClassResultsRef = useRef(fetchClassResults);
  const sendSmsToParentsRef = useRef(sendSmsToParents);

  useEffect(() => {
    fetchClassResultsRef.current = fetchClassResults;
  }, [fetchClassResults]);

  useEffect(() => {
    sendSmsToParentsRef.current = sendSmsToParents;
  }, [sendSmsToParents]);

  // ✅ store ALL results (unfiltered)
  const [allResults, setAllResults] = useState([]);

  // ✅ filter view (default ACTIVE)
  const [statusView, setStatusView] = useState("ACTIVE"); // "ACTIVE" | "ALL"

  const [isLoading, setIsLoading] = useState(false);
  const [sendingStatus, setSendingStatus] = useState({});
  const [sentStatus, setSentStatus] = useState({});

  // Load sent status from localStorage
  useEffect(() => {
    const savedSentStatus = localStorage.getItem(
      `sentStatus_${classId}_${examinationTypeId}`
    );
    if (savedSentStatus) {
      setSentStatus(JSON.parse(savedSentStatus));
    }
  }, [classId, examinationTypeId]);

  // Reset to ACTIVE when class/exam changes
  useEffect(() => {
    setStatusView("ACTIVE");
  }, [classId, examinationTypeId]);

  // ---------- Fetch Results ----------
  useEffect(() => {
    let mounted = true;

    const loadResults = async () => {
      if (!classId || !examinationTypeId) return;

      setIsLoading(true);
      try {
        const data = await fetchClassResultsRef.current(classId, examinationTypeId);

        if (!mounted) return;

        if (data && Array.isArray(data.data)) {
          const studentMap = {};

          data.data.forEach((item) => {
            if (!studentMap[item.studentId]) {
              studentMap[item.studentId] = {
                id: item.studentId,
                studentName: item.studentName,
                studentNumber: item.studentNumber,
                combination: item.combination || "N/A",
                className: item.className,
                classTitle: item.classTitle,
                examinationType: item.examinationType,
                // ✅ for ACTIVE/ALL filtering
                studentStatus: item.studentStatus || item.status || "",
                subjects: {},
              };
            }

            // Keep null for unfilled; 0 is a valid mark and included in average
            const marksRaw = item.marks != null && item.marks !== "" ? parseFloat(item.marks) : null;
            const marks = marksRaw !== null && !isNaN(marksRaw) ? marksRaw : null;
            // Always derive O-level grade from marks (same as /class_results)
            const grade = marks !== null ? getGrade(marks) : "";

            studentMap[item.studentId].subjects[item.subjectName] = {
              marks,
              grade,
            };
          });

          let processed = Object.values(studentMap).map((student) => {
            // Include subjects with a numeric mark (including 0); exclude null/unfilled
            const validSubjects = Object.values(student.subjects).filter(
              (s) => s?.marks !== null && s?.marks !== undefined
            );

            const subjectMarks = validSubjects.map((s) => s.marks ?? 0);
            const totalMarksRaw = subjectMarks.reduce((sum, m) => sum + m, 0);

            const count = validSubjects.length;
            const totalMarks = parseFloat(totalMarksRaw.toFixed(1));

            // O-level: complete when ≥ 7 subjects (same as /class_results)
            const hasCompleteResults = count >= 7;
            const average = hasCompleteResults
              ? parseFloat((totalMarks / count).toFixed(1))
              : "Incomplete";

            const points = determineDivisionPoints(student.subjects);
            const division = hasCompleteResults
              ? getDivisionLabel(points)
              : "Incomplete";
            const gpa = calculateStudentGpa(student.subjects);

            return {
              ...student,
              totalMarks,
              average,
              division,
              gpa,
              subjectCount: count,
              hasCompleteResults,
            };
          });

          // sort complete first by average; incomplete by subjectCount then totalMarks
          const completeResults = processed.filter((s) => s.hasCompleteResults);
          const incompleteResults = processed.filter((s) => !s.hasCompleteResults);

          completeResults.sort((a, b) => (b.average || 0) - (a.average || 0));

          incompleteResults.sort((a, b) => {
            if (b.subjectCount !== a.subjectCount) return b.subjectCount - a.subjectCount;
            return (b.totalMarks || 0) - (a.totalMarks || 0);
          });

          const sorted = [...completeResults, ...incompleteResults].map((stu, idx) => ({
            ...stu,
            position: idx + 1,
          }));

          setAllResults(sorted);
        } else {
          setAllResults([]);
        }
      } catch (e) {
        console.error("Error loading results:", e);
        if (mounted) setAllResults([]);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadResults();

    return () => {
      mounted = false;
    };
  }, [classId, examinationTypeId]);

  // ✅ view results (ACTIVE or ALL) + recompute position within the view
  const results = useMemo(() => {
    const isActive = (s) =>
      String(s || "")
        .trim()
        .toUpperCase() === "ACTIVE";

    const base =
      statusView === "ALL"
        ? allResults
        : allResults.filter((s) => isActive(s.studentStatus));

    return base.map((stu, idx) => ({
      ...stu,
      position: idx + 1,
    }));
  }, [allResults, statusView]);

  const classGpa = useMemo(() => {
    const validGpas = results
      .map((student) => student.gpa)
      .filter((gpa) => gpa !== null && gpa !== undefined && !Number.isNaN(Number(gpa)));

    if (validGpas.length === 0) return null;

    const total = validGpas.reduce((sum, gpa) => sum + Number(gpa), 0);
    return total / validGpas.length;
  }, [results]);

  const meta = allResults[0] || results[0] || {};

  const handleViewStudent = (row) => {
    navigate(`/alevel_student_results_marks/${row.id}/${classId}/${examinationTypeId}`);
  };

  const handleSendSMS = async (row) => {
    const studentId = row.id;
    setSendingStatus((prev) => ({ ...prev, [studentId]: true }));

    try {
      const subjectsSummary = Object.entries(row.subjects || {}).reduce(
        (acc, [subject, details]) => {
          const marks =
            details?.marks !== undefined && details?.marks !== null
              ? parseFloat(details.marks) || 0
              : 0;
          const grade = details?.grade || "";
          acc[subject] = { marks, grade };
          return acc;
        },
        {}
      );

      const payload = {
        studentName: row.studentName,
        studentNumber: row.studentNumber,
        subjects: subjectsSummary,
        totalMarks: parseFloat(row.totalMarks) || 0,
        average:
          row.average === "Incomplete"
            ? "Incomplete"
            : parseFloat(row.average?.toFixed?.(1)) || 0,
        position: row.position ? parseInt(row.position) : 0,
        division: row.division || "N/A",
        totalStudents: results.length, // ✅ view total (ACTIVE or ALL)
        className: row.className || "N/A",
        examinationType: row.examinationType || "N/A",
      };

      await sendSmsToParentsRef.current(studentId, payload);

      setSentStatus((prev) => {
        const newStatus = { ...prev, [studentId]: true };
        localStorage.setItem(
          `sentStatus_${classId}_${examinationTypeId}`,
          JSON.stringify(newStatus)
        );
        return newStatus;
      });
    } catch (error) {
      console.error("Error sending SMS", error);
    } finally {
      setSendingStatus((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const createColumns = () => {
    if (!results.length) return [];

    const subjectSet = new Set();
    results.forEach((student) =>
      Object.keys(student.subjects || {}).forEach((s) => subjectSet.add(s))
    );
    const subjectNames = Array.from(subjectSet);

    const columns = [
      {
        field: "studentName",
        headerName: "Student Name",
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.studentName}</Typography>
        ),
      },
      {
        field: "studentNumber",
        headerName: "Index Number",
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.studentNumber}</Typography>
        ),
      },
      {
        field: "combination",
        headerName: "Combination",
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.combination}</Typography>
        ),
      },
    ];

    subjectNames.forEach((subject) => {
      columns.push({
        field: `sub_${subject}`,
        headerName: subject,
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => {
          const s = row.subjects?.[subject];
          const marks = s?.marks;
          const grade = s?.grade ?? "";

          // null = unfilled → show blank; 0 = valid mark → display and use in average
          if (marks === null || marks === undefined) return <Box />;

          return (
            <Box>
              <Typography>{marks}</Typography>
              <Typography sx={{ fontSize: "0.75rem", opacity: 0.7 }}>
                {grade}
              </Typography>
            </Box>
          );
        },
      });
    });

    columns.push(
      {
        field: "totalMarks",
        headerName: "Total",
        flex: 0.6,
        minWidth: 90,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.totalMarks}</Typography>
        ),
      },
      {
        field: "average",
        headerName: "Average",
        flex: 0.6,
        minWidth: 90,
        renderCell: ({ row }) => (
          <Typography
            fontWeight="bold"
            color={row.average === "Incomplete" ? "error" : "inherit"}
          >
            {row.average}
          </Typography>
        ),
      },
      {
        field: "division",
        headerName: "Division",
        flex: 0.7,
        minWidth: 130,
        renderCell: ({ row }) => (
          <Box>
            <Typography fontWeight="bold" variant="body2">
              {String(row.division || "").split(" (")[0]}
            </Typography>
          </Box>
        ),
      },
      {
        field: "position",
        headerName: "Position",
        flex: 0.5,
        minWidth: 110,
        renderCell: ({ row }) => (
          <Typography
            fontWeight="bold"
            color={!row.hasCompleteResults ? "error" : "inherit"}
          >
            {row.position} out of {results.length}
            {!row.hasCompleteResults && " *"}
          </Typography>
        ),
      },
      {
        field: "action",
        headerName: "Action",
        flex: 0.8,
        minWidth: 180,
        renderCell: ({ row }) => (
          <Box display="flex" gap={1}>
            <IconButton color="secondary" onClick={() => handleViewStudent(row)}>
              <VisibilityOutlinedIcon />
            </IconButton>

            <Button
              variant="contained"
              color="secondary" // ✅ default secondary
              size="small"
              disabled={sendingStatus[row.id]}
              onClick={() => handleSendSMS(row)}
              sx={{
                ...(sentStatus[row.id] && {
                  backgroundColor: "#0A6927",
                  color: "white",
                  "&:hover": { backgroundColor: "#085A1F" },
                }),
                "&:disabled": {
                  backgroundColor: sendingStatus[row.id] ? "#ccc" : undefined,
                },
              }}
            >
              {sendingStatus[row.id]
                ? "Sending..."
                : sentStatus[row.id]
                ? "Sent"
                : "Send SMS"}
            </Button>
          </Box>
        ),
      }
    );

    return columns;
  };

  return (
    <Box m="20px">
      <Header
        title={`CLASS RESULTS FOR: ${meta.className || "N/A"} | CLASS TITLE: ${
          meta.classTitle || "N/A"
        } | EXAM: ${meta.examinationType || "N/A"}`}
      />

      {/* Class GPA + ACTIVE / ALL + Download PDF (same row) */}
      <Box
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
      >
        <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
          <Typography variant="h6" fontWeight={700} color={colors.grey[100]}>
            Class GPA
          </Typography>
          <Typography variant="h5" fontWeight={800} color="#000000">
            {formatGpa(classGpa)}
          </Typography>
          <Typography variant="body2" color={colors.grey[300]}>
            Average of student GPAs (each from 7 best subjects)
          </Typography>
        </Box>

        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <Button
            variant={statusView === "ACTIVE" ? "contained" : "outlined"}
            color="secondary"
            onClick={() => setStatusView("ACTIVE")}
          >
            ACTIVE
          </Button>

          <Button
            variant={statusView === "ALL" ? "contained" : "outlined"}
            color="secondary"
            onClick={() => setStatusView("ALL")}
          >
            ALL
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={() =>
              generateClassResultsPDF(
                results, // ✅ export what is currently displayed (ACTIVE or ALL)
                meta.className,
                meta.examinationType,
                meta.classTitle
              )
            }
            disabled={!results || results.length === 0}
            sx={{ ml: 1 }}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Box sx={{ height: "75vh" }}>
          <DataGrid rows={results} columns={createColumns()} getRowHeight={() => 80} pageSize={10} />
        </Box>
      )}
    </Box>
  );
};

export default AlevelClassResultsMarks100;
